import { AccountSettingsPayload } from '@account-users/account/models/AccountSettingsPayload';
import { LicensePackageCode } from '@account-users/account/models/License';
import { Permission } from '@account-users/account/models/Permission';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ValidatorFn,
} from '@angular/forms';
import { FormState } from '@shared';
import { Subject } from 'rxjs';

class LicensePackageValidators {
  public static packageChange(
    currentPackage: string,
    hasWawRole: boolean,
  ): ValidatorFn {
    return (control: AbstractControl): Record<string, string> => {
      if (
        currentPackage !== LicensePackageCode.Trial &&
        control?.value === LicensePackageCode.Trial
      ) {
        return {
          invalidChange:
            'Downgrading the license package to Trial is not supported',
        };
      }
      if (
        control?.value === LicensePackageCode.AllInOne ||
        control?.value === LicensePackageCode.Lite ||
        control?.value === LicensePackageCode.Essential
      ) {
        return {
          invalidChange:
            'Switching to deprecated All in One, Lite and Essential license packages is not supported',
        };
      }
      return null;
    };
  }
}

@Component({
  selector: 'app-select-order-field',
  templateUrl: './select-order-field.component.html',
  styleUrls: ['./select-order-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectOrderFieldComponent implements OnInit, OnDestroy {
  @Input() title = false;
  @Input() fullRowInput = false;
  @Input() useCancelButton = false;
  @Input() leftFieldlabel = '';
  @Input() buttonTooltip = '';
  @Input() disableTooltip = '';
  @Input() valueToUpdate = '';
  @Input() hasWawRole: boolean;
  @Input() canPlaceOrders = false;
  @Input() userPermissions: Permission[] = [];
  @Input() isUpdatingInput: boolean;
  @Input() inputValue = '';
  @Input() set disableFields(disable: boolean) {
    this._disabled = disable;
    if (this.form) {
      disable ? this.form.disable() : this.form.enable();
    } else {
      this.form = this.fb.group({
        [this.valueToUpdate]: this.fb.control({
          value: this.inputValue,
          disabled: !this.canPlaceOrders || disable,
        }),
        orderId: this.fb.control(
          { value: '', disabled: !this.canPlaceOrders || disable },
          [Validators.required, Validators.pattern(/^[A-Za-z0-9]{16}$/)],
        ),
      });
    }
  }
  @Input() options: { name: string; value: string }[] = [];
  @Output() editInput = new EventEmitter<AccountSettingsPayload>();
  @Output() changeInput = new EventEmitter<string>();
  @Output() cancelEdit = new EventEmitter<void>();

  public _disabled: boolean;
  public form: FormGroup;
  public FormState = FormState;
  public trialPackageError: string | null = null;

  private readonly _destroy$ = new Subject<void>();

  public get disabled() {
    return this._disabled;
  }

  public get value() {
    return this.form.get(this.valueToUpdate) as FormControl;
  }

  public get orderId() {
    return this.form.get('orderId') as FormControl;
  }

  public get disabledSubmit() {
    return (
      !this.canPlaceOrders ||
      this.isUpdatingInput ||
      this.form.invalid ||
      this.value.value === this.inputValue
    );
  }

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    if (!this.form) {
      this.form = this.fb.group({
        [this.valueToUpdate]: this.fb.control({
          value: this.inputValue,
          disabled: !this.canPlaceOrders,
        }),
        orderId: this.fb.control(
          { value: '', disabled: !this.canPlaceOrders },
          [Validators.required, Validators.pattern(/^[A-Za-z0-9]{16}$/)],
        ),
      });
      this.disabled ? this.form.disable() : this.form.enable();
    }

    if (this.form) {
      if (this.valueToUpdate === 'licensePackage') {
        this.value.setValidators(
          LicensePackageValidators.packageChange(
            this.inputValue,
            this.hasWawRole,
          ),
        );
      }
    }
  }

  public selectChange(event: string) {
    this.changeInput.emit(event);
  }

  public onSubmit($event: Event) {
    $event.preventDefault();

    if (this.form.valid && this.value.touched) {
      this.editInput.emit({
        [this.valueToUpdate]: this.value.value,
        orderId: this.orderId.value,
      });

      this.orderId.reset();
    }
  }

  public cancel($event: Event) {
    $event.preventDefault();
    this.orderId.reset();
    this.cancelEdit.emit();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
